<main>
  <loading-overlay [isLoading]="isLoading() > 0 || isLoadingAvailableRolls()"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>
  <!-- Header -->
  <header>
    <div class="headline">
      <h1>
        @if (dispatchInstruction()) {
          {{ 'Dispatch_Instruction_Title' | translateWF: 'Dispatch Instruction' }}: {{ dispatchInstruction()?.number }}
          @if (dispatchInstruction()?.invoiceNumber) {
            <span class="text-gray-500">· {{ 'Dispatch_Invoice_Number' | translateWF: 'Invoice Number' }}: {{ dispatchInstruction()?.invoiceNumber }}</span>
          }
        } @else {
          {{ 'Dispatch_Instruction_New' | translateWF: 'New Dispatch Instruction' }}
        }
      </h1>
      <csl-dropdown [formControl]="status" [enumObject]="statusEnumObject" [enumValueOptions]="availableStatuses()" [disabled]="isReadOnlyMode()" />
    </div>
    <div class="buttons">
      @if (!isNewRecord() && lines().length > 0) {
        <p-button
          [label]="'Dispatch_Fabric_Customizations' | translateWF: 'Fabric Customizations'"
          icon="pi pi-cog"
          variant="outlined"
          (onClick)="onOpenFabricCustomizationsDialog()"
        />
      }
      @if (!isNewRecord()) {
        @if (isChemicalType()) {
          <!-- Chemical dispatches only support the Commercial Invoice (price-bearing, so price viewers only) -->
          @if (canViewPrices) {
            @if (!isCompleted()) {
              <p-button
                [label]="'Dispatch_Download_Pre_Invoice' | translateWF: 'Download Pre-Invoice'"
                icon="pi pi-download"
                variant="outlined"
                (onClick)="downloadSingleDocument('commercial-invoice-pre')"
                [loading]="isDownloadingAll()"
                [disabled]="isDownloadingAll()"
              />
            } @else {
              <p-button
                [label]="'Dispatch_Download_Invoice' | translateWF: 'Download Invoice'"
                icon="pi pi-download"
                (onClick)="downloadSingleDocument('commercial-invoice')"
                [loading]="isDownloadingAll()"
                [disabled]="isDownloadingAll()"
              />
            }
          }
        } @else if (canViewPrices) {
          @if (!isCompleted()) {
            <p-splitButton
              [label]="'Dispatch_Pre_Invoice_Documents' | translateWF: 'Pre-Invoicing Documents'"
              icon="pi pi-download"
              styleClass="p-button-outlined"
              (onClick)="downloadPreInvoiceDocuments()"
              [model]="preInvoiceDocumentItems()"
              [disabled]="isDownloadingAll()"
            />
          }
          @if (isCompleted()) {
            <p-splitButton
              [label]="'Dispatch_Download_All' | translateWF: 'Download All Documents'"
              icon="pi pi-download"
              (onClick)="downloadAllDocuments()"
              [model]="finalDocumentItems()"
              [disabled]="isDownloadingAll()"
            />
          }
        } @else {
          <p-button
            [label]="'Dispatch_Download_Packing_List' | translateWF: 'Download Packing List'"
            icon="pi pi-download"
            variant="outlined"
            (onClick)="downloadPackingListOnly()"
            [loading]="isDownloadingAll()"
            [disabled]="isDownloadingAll()"
          />
        }
      }
      <p-button [label]="'Common_Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="onCancel()" />
    </div>
  </header>

  <!-- Content -->
  <div class="content">
    <div class="grid-12 overflow-hidden">
      <!-- Left Column: Scrollable Content -->
      <div class="span-12-8-8 col scroller-y">
        <!-- Initial Creation Form (Step 1 - New Dispatch Instruction) -->
        @if (isNewRecord()) {
          <p-panel [header]="'Dispatch_Initial_Setup' | translateWF: 'Initial Setup'" [toggleable]="false">
            <p-card data-variant="flat">
              <div class="row-start-center gap-3 mb-4">
                <span class="font-semibold">{{ 'Dispatch_Type' | translateWF: 'Type' }}:</span>
                @if (selectedDispatchType() === DispatchInstructionType.BGrade) {
                  <p-tag severity="warn" icon="pi pi-tag" [value]="'Dispatch_Type_BGrade' | translateWF: 'B-Grade'" />
                } @else if (selectedDispatchType() === DispatchInstructionType.GreigeOnly) {
                  <p-tag severity="info" icon="pi pi-box" [value]="'Dispatch_Type_GreigeOnly' | translateWF: 'Greige Only'" />
                } @else {
                  <p-tag severity="info" icon="pi pi-file" [value]="'Dispatch_Type_Standard' | translateWF: 'Standard'" />
                }
              </div>

              <p class="mb-4 text-gray-600">
                {{
                  'Dispatch_Initial_Setup_Description'
                    | translateWF
                      : 'Select the client and shipping address to create
              the dispatch instruction.'
                }}
              </p>
              <form [formGroup]="initialForm" class="grid-12">
                <!-- BGrade Work Order Selection (only for BGrade type) -->
                @if (selectedDispatchType() === DispatchInstructionType.BGrade) {
                  <div class="span-12">
                    <div class="form-box">
                      <label>{{ 'Dispatch_BGrade_WorkOrder' | translateWF: 'B-Grade Work Order' }} *</label>
                      <p-autoComplete
                        [ngModel]="selectedBGradeWorkOrder()"
                        [ngModelOptions]="{ standalone: true }"
                        #bGradeWorkOrderSearchRef="cslServerSearch"
                        [cslServerSearch]="fetchBGradeWorkOrders"
                        [suggestions]="bGradeWorkOrderSearchRef.results()"
                        (onSelect)="onBGradeWorkOrderSelect($event.value)"
                        (onClear)="onBGradeWorkOrderClear()"
                        field="orderNumber"
                        [dropdown]="true"
                        [forceSelection]="true"
                        placeholder="{{ 'Dispatch_Search_BGrade_WorkOrder' | translateWF: 'Search B-Grade work order...' }}"
                        fluid
                        class="w-full"
                      >
                        <ng-template let-workOrder pTemplate="item">
                          <div class="col">
                            <span class="font-semibold">{{ workOrder.orderNumber }}</span>
                            <small class="text-gray-500">{{ workOrder.customer?.name }}</small>
                          </div>
                        </ng-template>
                      </p-autoComplete>
                      <small class="text-gray-500 mt-1">
                        {{
                          'Dispatch_BGrade_WorkOrder_Help'
                            | translateWF
                              : 'Select the B-Grade work order to link this
                    dispatch to'
                        }}
                      </small>
                    </div>
                  </div>
                }

                <!-- GreigeOnly Work Order Selection (only for GreigeOnly type) -->
                @if (selectedDispatchType() === DispatchInstructionType.GreigeOnly) {
                  <div class="span-12">
                    <div class="form-box">
                      <label>{{ 'Dispatch_GreigeOnly_WorkOrder' | translateWF: 'Greige Only Work Order' }} *</label>
                      <p-autoComplete
                        [ngModel]="selectedGreigeOnlyWorkOrder()"
                        [ngModelOptions]="{ standalone: true }"
                        #greigeOnlyWorkOrderSearchRef="cslServerSearch"
                        [cslServerSearch]="fetchGreigeOnlyWorkOrders"
                        [suggestions]="greigeOnlyWorkOrderSearchRef.results()"
                        (onSelect)="onGreigeOnlyWorkOrderSelect($event.value)"
                        (onClear)="onGreigeOnlyWorkOrderClear()"
                        field="orderNumber"
                        [dropdown]="true"
                        [forceSelection]="true"
                        placeholder="{{ 'Dispatch_Search_GreigeOnly_WorkOrder' | translateWF: 'Search Greige Only work order...' }}"
                        fluid
                        class="w-full"
                      >
                        <ng-template let-workOrder pTemplate="item">
                          <div class="col">
                            <span class="font-semibold">{{ workOrder.orderNumber }}</span>
                            <small class="text-gray-500">{{ workOrder.customer?.name }}</small>
                          </div>
                        </ng-template>
                      </p-autoComplete>
                      <small class="text-gray-500 mt-1">
                        {{
                          'Dispatch_GreigeOnly_WorkOrder_Help'
                            | translateWF
                              : 'Select the Greige Only work order to link
                    this dispatch to'
                        }}
                      </small>
                    </div>
                  </div>
                }

                <div class="span-6">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Client' | translateWF: 'Client' }} *</label>
                    @if (isSingleLineCreateType()) {
                      <input pInputText [value]="initialForm.controls.client.value?.name || ''" [disabled]="true" class="w-full" />
                      <small class="text-gray-500 mt-1">
                        {{ 'Dispatch_Client_Auto_Selected' | translateWF: 'Auto-selected from work order' }}
                      </small>
                    } @else {
                      <p-autoComplete
                        formControlName="client"
                        #clientSearchRef="cslServerSearch"
                        [cslServerSearch]="fetchClients"
                        [suggestions]="clientSearchRef.results()"
                        (onSelect)="onClientSelect($event.value)"
                        (onClear)="onClientClear()"
                        field="name"
                        [dropdown]="true"
                        [forceSelection]="true"
                        placeholder="{{ 'Dispatch_Search_Client' | translateWF: 'Search client...' }}"
                        fluid
                        class="w-full"
                        [ngClass]="{ 'ng-invalid ng-dirty': initialForm.controls.client.invalid && initialForm.controls.client.touched }"
                      >
                        <ng-template let-client pTemplate="item">
                          <div class="col">
                            <span class="font-semibold">{{ client.name }}</span>
                            @if (client.code) {
                              <small class="text-gray-500">{{ client.code }}</small>
                            }
                          </div>
                        </ng-template>
                      </p-autoComplete>
                    }
                  </div>
                </div>

                <div class="span-6">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Ship_To_Address' | translateWF: 'Ship To Address' }} *</label>
                    <p-select
                      formControlName="shipToAddress"
                      [options]="shippingAddresses()"
                      appendTo="body"
                      optionLabel="address1"
                      [disabled]="!selectedClient()"
                      placeholder="{{ 'Dispatch_Select_Address' | translateWF: 'Select shipping address' }}"
                      class="w-full"
                      [ngClass]="{
                        'ng-invalid ng-dirty': initialForm.controls.shipToAddress.invalid && initialForm.controls.shipToAddress.touched,
                      }"
                    >
                      <ng-template let-addr pTemplate="selectedItem">
                        @if (addr) {
                          <span>{{ addr.address1 }}, {{ addr.city }}</span>
                        }
                      </ng-template>
                      <ng-template let-addr pTemplate="item">
                        <div class="col">
                          <span>{{ addr.address1 }}</span>
                          <small class="text-gray-500">{{ addr.city }}, {{ addr.stateOrProvince }} {{ addr.zipCode }}</small>
                        </div>
                      </ng-template>
                    </p-select>
                  </div>
                </div>

                <div class="span-12 row-sm justify-end">
                  <p-button [label]="'Common_Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="onCancel()" />
                  <p-button
                    [label]="'Dispatch_Create' | translateWF: 'Create Dispatch Instruction'"
                    icon="pi pi-check"
                    (onClick)="onSaveInitial()"
                    [disabled]="initialForm.invalid"
                  />
                </div>
              </form>
            </p-card>
          </p-panel>
        }

        <!-- The rest of the content only shows when NOT a new record -->
        @if (!isNewRecord()) {
          <div class="row-start-center">
            @if (isAddMode()) {
              <p-tag severity="info" icon="pi pi-plus">
                {{ 'Dispatch_Mode_Add' | translateWF: 'Add Mode' }}
              </p-tag>
            } @else if (isScanMode()) {
              <p-tag severity="warn" icon="pi pi-barcode">
                {{ 'Dispatch_Mode_Scan' | translateWF: 'Scan Mode' }}
              </p-tag>
            } @else {
              <p-tag severity="secondary" icon="pi pi-eye">
                {{ 'Dispatch_Mode_View' | translateWF: 'View Only' }}
              </p-tag>
            }

            @if (dispatchInstruction()?.type === DispatchInstructionType.BGrade) {
              <p-tag severity="warn" icon="pi pi-tag">
                {{ 'Dispatch_Type_BGrade' | translateWF: 'B-Grade' }}
              </p-tag>
            }
            @if (dispatchInstruction()?.type === DispatchInstructionType.GreigeOnly) {
              <p-tag severity="info" icon="pi pi-box">
                {{ 'Dispatch_Type_GreigeOnly' | translateWF: 'Greige Only' }}
              </p-tag>
            }
            @if (isChemicalType()) {
              <p-tag severity="info" icon="pi pi-flask">
                {{ 'Dispatch_Type_Chemical' | translateWF: 'Chemical' }}
              </p-tag>
            }
          </div>

          <!-- Shipping Information Section -->

          <p-panel [header]="'Dispatch_Shipping_Info' | translateWF: 'Shipping Information'" [toggleable]="true" [collapsed]="false">
            <p-card data-variant="flat">
              <form [formGroup]="shippingInfoForm" class="grid-12">
                <div class="span-6">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Ship_To_Customer' | translateWF: 'Ship To Customer' }}</label>
                    <p-autoComplete
                      [(ngModel)]="shippingToClientInput"
                      [ngModelOptions]="{ standalone: true }"
                      #shippingToSearchRef="cslServerSearch"
                      [cslServerSearch]="fetchClients"
                      [suggestions]="shippingToSearchRef.results()"
                      [searchOptions]="{ minLength: 2 }"
                      (onSelect)="onShippingToClientSelect($event)"
                      (onClear)="onShippingToClientClear()"
                      field="name"
                      [dropdown]="true"
                      placeholder="{{ 'Dispatch_Search_Customer' | translateWF: 'Search customer...' }}"
                      fluid
                      class="w-full"
                    >
                      <ng-template let-client pTemplate="item">
                        <div class="col">
                          <span class="font-semibold">{{ client.name }}</span>
                          @if (client.code) {
                            <small class="text-gray-500">{{ client.code }}</small>
                          }
                        </div>
                      </ng-template>
                    </p-autoComplete>
                    <small class="text-gray-500">{{
                      'Dispatch_Ship_To_Customer_Help'
                        | translateWF
                          : 'Leave empty to use
                    billing customer'
                    }}</small>
                  </div>
                </div>

                <div class="span-6">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Ship_To_Address' | translateWF: 'Ship To Address' }}</label>
                    <p-select
                      [options]="existingClientAddresses()"
                      [ngModel]="selectedShipToAddress()"
                      [ngModelOptions]="{ standalone: true }"
                      (onChange)="onShipToAddressChange($event.value)"
                      appendTo="body"
                      optionLabel="address1"
                      placeholder="{{ 'Dispatch_Select_Address' | translateWF: 'Select shipping address' }}"
                      class="w-full"
                    >
                      <ng-template let-addr pTemplate="selectedItem">
                        @if (addr) {
                          <span>{{ addr.address1 }}, {{ addr.city }}</span>
                        }
                      </ng-template>
                      <ng-template let-addr pTemplate="item">
                        <div class="col">
                          <span>{{ addr.address1 }}</span>
                          <small class="text-gray-500">{{ addr.city }}, {{ addr.stateOrProvince }} {{ addr.zipCode }}</small>
                        </div>
                      </ng-template>
                    </p-select>
                  </div>
                </div>

                <div class="span-6">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Ship_To_Representative' | translateWF: 'Contact' }}</label>
                    <p-select
                      [options]="existingClientRepresentatives()"
                      [ngModel]="selectedShippingToRepresentative()"
                      [ngModelOptions]="{ standalone: true }"
                      (onChange)="onShippingToRepresentativeChange($event.value)"
                      appendTo="body"
                      optionLabel="firstName"
                      placeholder="{{ 'Dispatch_Select_Representative' | translateWF: 'Select contact' }}"
                      class="w-full"
                    >
                      <ng-template let-rep pTemplate="selectedItem">
                        @if (rep) {
                          <span>{{ rep.firstName }} {{ rep.lastName }}</span>
                        }
                      </ng-template>
                      <ng-template let-rep pTemplate="item">
                        <div class="col">
                          <span>{{ rep.firstName }} {{ rep.lastName }}</span>
                          <small class="text-gray-500">{{ rep.email }}</small>
                        </div>
                      </ng-template>
                    </p-select>
                  </div>
                </div>

                <div class="span-6">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Voyage_ID' | translateWF: 'Voyage ID' }}</label>
                    <input type="text" pInputText formControlName="voyageId" placeholder="{{ 'Dispatch_Enter_Voyage_ID' | translateWF: 'Enter voyage ID' }}" class="w-full" />
                  </div>
                </div>

                <div class="span-6">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Booking' | translateWF: 'Booking' }}</label>
                    <input type="text" pInputText formControlName="booking" placeholder="{{ 'Dispatch_Enter_Booking' | translateWF: 'Enter booking reference' }}" class="w-full" />
                  </div>
                </div>

                <!-- Save Button (Voyage/Booking only - address saves immediately) -->
                <div class="span-12 row-end-center gap-2">
                  @if (shippingInfoForm.dirty) {
                    <span class="text-orange-500 text-sm"><i class="pi pi-exclamation-circle mr-1"></i>{{ 'Common_Changes_Not_Saved' | translateWF: 'Changes Not Saved' }}</span>
                  }
                  <p-button [label]="'Common_Save' | translateWF: 'Save'" icon="pi pi-save" [disabled]="!isShippingDataReady()" (onClick)="onSaveShippingInfo()" />
                </div>
              </form>
            </p-card>
          </p-panel>

          <!-- Consignee Information Section -->
          <p-panel [header]="'Dispatch_Consignee_Info' | translateWF: 'Consignee Information'" [toggleable]="true" [collapsed]="!hasConsignee()">
            <p-card data-variant="flat">
              <div class="grid-12">
                <div class="span-6">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Consignee_Customer' | translateWF: 'Consignee Customer' }}</label>
                    <p-autoComplete
                      [(ngModel)]="consigneeClientInput"
                      #consigneeSearchRef="cslServerSearch"
                      [cslServerSearch]="fetchClients"
                      [suggestions]="consigneeSearchRef.results()"
                      [searchOptions]="{ minLength: 2 }"
                      (onSelect)="onConsigneeClientSelect($event)"
                      (onClear)="onConsigneeClientClear()"
                      field="name"
                      [dropdown]="true"
                      placeholder="{{ 'Dispatch_Search_Customer' | translateWF: 'Search customer...' }}"
                      fluid
                      class="w-full"
                    >
                      <ng-template let-client pTemplate="item">
                        <div class="col">
                          <span class="font-semibold">{{ client.name }}</span>
                          @if (client.code) {
                            <small class="text-gray-500">{{ client.code }}</small>
                          }
                        </div>
                      </ng-template>
                    </p-autoComplete>
                    <small class="text-gray-500">{{
                      'Dispatch_Consignee_Help'
                        | translateWF
                          : 'Leave empty if no consignee
                    is needed'
                    }}</small>
                  </div>
                </div>

                <div class="span-6">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Consignee_Address' | translateWF: 'Consignee Address' }}</label>
                    <p-select
                      [options]="consigneeClientAddresses()"
                      [ngModel]="selectedConsigneeAddress()"
                      (onChange)="onConsigneeAddressChange($event.value)"
                      appendTo="body"
                      optionLabel="address1"
                      placeholder="{{ 'Dispatch_Select_Address' | translateWF: 'Select consignee address' }}"
                      class="w-full"
                    >
                      <ng-template let-addr pTemplate="selectedItem">
                        @if (addr) {
                          <span>{{ addr.address1 }}, {{ addr.city }}</span>
                        }
                      </ng-template>
                      <ng-template let-addr pTemplate="item">
                        <div class="col">
                          <span>{{ addr.address1 }}</span>
                          <small class="text-gray-500">{{ addr.city }}, {{ addr.stateOrProvince }} {{ addr.zipCode }}</small>
                        </div>
                      </ng-template>
                    </p-select>
                  </div>
                </div>

                <div class="span-12 row-end-stretch">
                  <p-button [label]="'Common_Save' | translateWF: 'Save'" icon="pi pi-save" (onClick)="onSaveConsigneeInfo()" />
                </div>
              </div>
            </p-card>
          </p-panel>

          <!-- Billing Information Section -->
          <p-panel [header]="'Dispatch_Billing_Info' | translateWF: 'Billing Information'" [toggleable]="true" [collapsed]="false">
            <p-card data-variant="flat">
              <div class="grid-12">
                <div class="span-6">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Bill_To_Customer' | translateWF: 'Bill To Customer' }}</label>
                    <p-autoComplete
                      [(ngModel)]="billingToClientInput"
                      #billingToSearchRef="cslServerSearch"
                      [cslServerSearch]="fetchClients"
                      [suggestions]="billingToSearchRef.results()"
                      [searchOptions]="{ minLength: 2 }"
                      (onSelect)="onBillingToClientSelect($event)"
                      (onClear)="onBillingToClientClear()"
                      field="name"
                      [dropdown]="true"
                      placeholder="{{ 'Dispatch_Search_Customer' | translateWF: 'Search customer...' }}"
                      fluid
                      class="w-full"
                    >
                      <ng-template let-client pTemplate="item">
                        <div class="col">
                          <span class="font-semibold">{{ client.name }}</span>
                          @if (client.code) {
                            <small class="text-gray-500">{{ client.code }}</small>
                          }
                        </div>
                      </ng-template>
                    </p-autoComplete>
                    <small class="text-gray-500">{{
                      'Dispatch_Bill_To_Customer_Help'
                        | translateWF
                          : 'Leave empty to use
                    default client'
                    }}</small>
                  </div>
                </div>

                <div class="span-6">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Bill_To_Address' | translateWF: 'Bill To Address' }}</label>
                    <p-select
                      [options]="billingClientAddresses()"
                      [ngModel]="selectedBillToAddress()"
                      (onChange)="onBillToAddressChange($event.value)"
                      appendTo="body"
                      optionLabel="address1"
                      placeholder="{{ 'Dispatch_Select_Address' | translateWF: 'Select billing address' }}"
                      class="w-full"
                    >
                      <ng-template let-addr pTemplate="selectedItem">
                        @if (addr) {
                          <span>{{ addr.address1 }}, {{ addr.city }}</span>
                        }
                      </ng-template>
                      <ng-template let-addr pTemplate="item">
                        <div class="col">
                          <span>{{ addr.address1 }}</span>
                          <small class="text-gray-500">{{ addr.city }}, {{ addr.stateOrProvince }} {{ addr.zipCode }}</small>
                        </div>
                      </ng-template>
                    </p-select>
                  </div>
                </div>

                <div class="span-12">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Invoice_Comment' | translateWF: 'Invoice Comment' }}</label>
                    <textarea
                      pInputTextarea
                      [formControl]="invoiceComment"
                      rows="3"
                      class="w-full"
                      placeholder="{{ 'Dispatch_Invoice_Comment_Placeholder' | translateWF: 'Enter comments for the invoice...' }}"
                    ></textarea>
                    <small class="text-gray-500">{{
                      'Dispatch_Invoice_Comment_Help'
                        | translateWF
                          : 'This comment will
                    appear on the commercial invoice'
                    }}</small>
                  </div>
                </div>

                <div class="span-12 row-end-center gap-2">
                  @if (invoiceComment.dirty) {
                    <span class="text-orange-500 text-sm"><i class="pi pi-exclamation-circle mr-1"></i>{{ 'Common_Changes_Not_Saved' | translateWF: 'Changes Not Saved' }}</span>
                  }
                  <p-button [label]="'Common_Save' | translateWF: 'Save'" icon="pi pi-save" (onClick)="onSaveBillingInfo()" />
                </div>
              </div>
            </p-card>
          </p-panel>

          <!-- Extra Tolerance Section -->
          <p-panel [header]="'Dispatch_Extra_Tolerance' | translateWF: 'Tolerance'" [toggleable]="true" [collapsed]="false">
            <p-card data-variant="flat">
              <div class="grid-12">
                <div class="span-4">
                  <label class="block mb-1 font-medium">{{ 'Dispatch_Tolerance_Client_Base' | translateWF: 'Client Base' }}</label>
                  <p-inputgroup>
                    <input pInputText [value]="clientBaseOveragePercentage() !== null ? clientBaseOveragePercentage() : '—'" readonly class="w-full" />
                    <p-inputgroup-addon>%</p-inputgroup-addon>
                  </p-inputgroup>
                </div>

                <div class="span-4">
                  <label class="block mb-1 font-medium">{{ 'Dispatch_Tolerance_Extra' | translateWF: 'Extra' }}</label>
                  <p-inputgroup>
                    <p-inputNumber [formControl]="extraTolerancePercentage" [min]="0" [minFractionDigits]="0" [maxFractionDigits]="4" [disabled]="!isDraft()" class="w-full" />
                    <p-inputgroup-addon>%</p-inputgroup-addon>
                  </p-inputgroup>
                </div>

                <div class="span-4">
                  <label class="block mb-1 font-medium">{{ 'Dispatch_Tolerance_Total' | translateWF: 'Total' }}</label>
                  <p-inputgroup>
                    <input pInputText [value]="(clientBaseOveragePercentage() ?? 0) + (extraTolerancePercentage.value ?? 0)" readonly class="w-full" />
                    <p-inputgroup-addon>%</p-inputgroup-addon>
                  </p-inputgroup>
                </div>

                <!-- Comment (required when extra % is set) -->
                <div class="span-12">
                  <label class="block mb-1 font-medium">
                    {{ 'Dispatch_Tolerance_Comment' | translateWF: 'Comment' }}
                    @if (extraTolerancePercentage.value) {
                      <span class="text-red-500 ml-1">*</span>
                    }
                  </label>
                  <textarea
                    pTextarea
                    [formControl]="extraToleranceComment"
                    [attr.readonly]="!isDraft() ? true : null"
                    rows="2"
                    class="w-full"
                    [placeholder]="'Dispatch_Tolerance_Comment_Placeholder' | translateWF: 'e.g. It was agreed with client'"
                  ></textarea>
                </div>

                @if (isDraft()) {
                  <div class="span-12 row-end-center gap-2">
                    @if (extraTolerancePercentage.dirty || extraToleranceComment.dirty) {
                      <span class="text-orange-500 text-sm"><i class="pi pi-exclamation-circle mr-1"></i>{{ 'Common_Changes_Not_Saved' | translateWF: 'Changes Not Saved' }}</span>
                    }
                    <p-button [label]="'Common_Save' | translateWF: 'Save'" icon="pi pi-save" (onClick)="onSaveExtraTolerance()" />
                  </div>
                }
              </div>
            </p-card>
          </p-panel>

          <!-- Notes Section -->
          <p-panel [header]="'Dispatch_Notes' | translateWF: 'Notes'" [toggleable]="true" [collapsed]="true">
            <p-card data-variant="flat">
              <csl-standard-notes [userLite]="userLite!" [notes]="dispatchInstruction()?.notes || []" [viewOnly]="isReadOnlyMode()" (noteAdded)="onNoteAdded()" />
            </p-card>
          </p-panel>

          <!-- Priority and Date Section -->
          <p-panel [header]="'Dispatch_Priority_Date' | translateWF: 'Priority & Date'" [toggleable]="true" [collapsed]="false">
            <p-card data-variant="flat">
              <loading-overlay [isLoading]="isPriorityLoading() && !showPriorityDialog()" />
              <!-- Current Priority Display (when priority is already set) -->
              @if (hasPriorityAndDate()) {
                <div class="p-3 surface-ground border-round">
                  <div class="grid-12">
                    <div class="span-6">
                      <div class="text-gray-500 text-sm">{{ 'Dispatch_Scheduled_Date' | translateWF: 'Scheduled Date' }}</div>
                      <div class="font-semibold text-lg">{{ dispatchInstruction()?.dateAndPriorityKey?.date | date: 'mediumDate' }}</div>
                    </div>
                    <div class="span-6">
                      <div class="text-gray-500 text-sm">{{ 'Dispatch_Priority' | translateWF: 'Priority' }}</div>
                      <div class="font-semibold text-lg">#{{ dispatchInstruction()?.dateAndPriorityKey?.priority }}</div>
                    </div>
                  </div>
                  @if (canEditPriority()) {
                    <div class="row-sm mt-3">
                      <p-button [label]="'Dispatch_Change_Priority' | translateWF: 'Change Priority'" icon="pi pi-sort-alt" variant="outlined" (onClick)="onOpenPriorityDialog()" />
                      <p-button
                        [label]="'Dispatch_Remove_Priority' | translateWF: 'Remove'"
                        icon="pi pi-times"
                        variant="outlined"
                        severity="danger"
                        (onClick)="onRemovePriorityAndDate()"
                      />
                    </div>
                  }
                </div>
              } @else {
                <!-- Set Priority Form (only when editable and NO priority set yet) -->
                @if (canEditPriority()) {
                  <div class="grid-12">
                    <div class="span-6">
                      <div class="form-box">
                        <label>{{ 'Dispatch_Select_Date' | translateWF: 'Select Date' }}</label>
                        <p-datePicker
                          [formControl]="priorityDate"
                          [showIcon]="true"
                          dateFormat="yy-mm-dd"
                          placeholder="{{ 'Dispatch_Select_Date_Placeholder' | translateWF: 'Select a date...' }}"
                          class="w-full"
                          (onSelect)="onPriorityDateSelect()"
                        />
                      </div>
                    </div>
                    <div class="span-6 flex items-end">
                      @if (priorityDate.value) {
                        <p-button
                          [label]="'Dispatch_Set_Priority' | translateWF: 'Set Priority'"
                          icon="pi pi-check"
                          (onClick)="onSetPriorityAndDate()"
                          [loading]="isPriorityLoading()"
                        />
                      }
                    </div>
                  </div>
                } @else {
                  <p-message severity="info" [text]="'Dispatch_Priority_Readonly' | translateWF: 'Priority cannot be changed for completed dispatches'" />
                }
              }
            </p-card>
          </p-panel>

          <!-- Fabric Customizations Dialog -->
          <p-dialog
            [header]="'Dispatch_Fabric_Customizations' | translateWF: 'Fabric Customizations'"
            [(visible)]="showFabricCustomizationsDialog"
            [modal]="true"
            [style]="{ width: '1200px' }"
            [closable]="true"
            (onHide)="onCloseFabricCustomizationsDialog()"
          >
            <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>

            <p class="mb-4 text-gray-600">
              {{
                'Dispatch_Fabric_Customizations_Description'
                  | translateWF
                    : 'Select rolls and assign a different unit price. Rolls not assigned to any split will use the
            default WorkOrder price.'
              }}
            </p>

            @if (editableFabricCustomizations().length > 0) {
              <!-- Cross-Production-Card quick assign: filter + select rolls from several PCs, one price for all -->
              <p-panel [toggleable]="true" [collapsed]="false" styleClass="mb-4" [header]="'Dispatch_Cross_PC_Assign' | translateWF: 'Assign Price Across Production Cards'">
                <p-card data-variant="compact">
                  <p class="text-sm text-gray-600 mb-3">
                    {{
                      'Dispatch_Cross_PC_Assign_Description'
                        | translateWF
                          : 'Filter and select rolls from multiple production
                cards to assign them a single price at once.'
                    }}
                  </p>
                  <div class="grid-4 gap-3 mb-3">
                    <input
                      type="text"
                      pInputText
                      [ngModel]="crossPcFilterFabricCode()"
                      (ngModelChange)="crossPcFilterFabricCode.set($event)"
                      placeholder="{{ 'Dispatch_Fabric_Code' | translateWF: 'Fabric Code' }}"
                    />
                    <input
                      type="text"
                      pInputText
                      [ngModel]="crossPcFilterFabricName()"
                      (ngModelChange)="crossPcFilterFabricName.set($event)"
                      placeholder="{{ 'Dispatch_Fabric_Name' | translateWF: 'Fabric Name' }}"
                    />
                    <input
                      type="text"
                      pInputText
                      [ngModel]="crossPcFilterColor()"
                      (ngModelChange)="crossPcFilterColor.set($event)"
                      placeholder="{{ 'Dispatch_Color' | translateWF: 'Color' }}"
                    />
                    <input
                      type="text"
                      pInputText
                      [ngModel]="crossPcFilterProductionCard()"
                      (ngModelChange)="crossPcFilterProductionCard.set($event)"
                      placeholder="{{ 'Dispatch_Production_Card' | translateWF: 'Production Card' }}"
                    />
                  </div>

                  @if (filteredCrossPcRolls().length > 0) {
                    <p-table
                      [value]="filteredCrossPcRolls()"
                      [scrollable]="true"
                      scrollHeight="250px"
                      selectionMode="multiple"
                      [selection]="crossPcSelectedRows"
                      (selectionChange)="onCrossPcSelectionChange($event)"
                      dataKey="rollNumber"
                    >
                      <ng-template pTemplate="header">
                        <tr>
                          <th style="width: 3rem">
                            <p-tableHeaderCheckbox />
                          </th>
                          <th>{{ 'Dispatch_Roll_Number' | translateWF: 'Roll' }}</th>
                          <th>{{ 'Dispatch_Fabric' | translateWF: 'Fabric' }}</th>
                          <th>{{ 'Dispatch_Color' | translateWF: 'Color' }}</th>
                          <th>{{ 'Dispatch_Production_Card' | translateWF: 'Production Card' }}</th>
                          <th class="text-right">{{ 'Dispatch_Weight_Lbs' | translateWF: 'LBS' }}</th>
                          <th class="text-right">{{ 'Dispatch_Weight_Yds' | translateWF: 'YDS' }}</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-row>
                        <tr>
                          <td>
                            <p-tableCheckbox [value]="row" />
                          </td>
                          <td>{{ row.rollNumber }}</td>
                          <td>{{ getFabricDescription(row.fabricCode) }} - {{ row.fabricCode }}</td>
                          <td>{{ getColorName(row.colorReference) }}</td>
                          <td>{{ row.productionCardNumber }}</td>
                          <td class="text-right">{{ row.weightInLbs | number: '1.2-2' }}</td>
                          <td class="text-right">{{ row.yards | number: '1.2-2' }}</td>
                        </tr>
                      </ng-template>
                    </p-table>

                    <div class="row-start-center gap-3 mt-2 p-2 bg-gray-50 rounded">
                      <p-tag [value]="crossPcSelectedRows.length + ' rolls'" severity="info" />
                      <span class="text-sm">{{ 'Dispatch_Unit_Price' | translateWF: 'Unit Price' }}:</span>
                      <p-inputNumber
                        [(ngModel)]="crossPcUnitPrice"
                        mode="decimal"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="4"
                        [placeholder]="'Dispatch_Price' | translateWF: 'Price'"
                        [inputStyle]="{ width: '120px' }"
                      />
                      <p-button
                        [label]="'Dispatch_Add_Split' | translateWF: 'Add Split'"
                        icon="pi pi-plus"
                        size="small"
                        [disabled]="crossPcSelectedRows.length === 0"
                        (onClick)="addCrossPcSplit()"
                      />
                    </div>
                  } @else {
                    <p class="text-sm text-gray-500 italic">
                      {{ 'Dispatch_All_Rolls_Assigned' | translateWF: 'All rolls are assigned to splits.' }}
                    </p>
                  }
                </p-card>
              </p-panel>

              @for (customization of editableFabricCustomizations(); track getCustomizationKey(customization)) {
                <p-panel [toggleable]="true" [collapsed]="!isCustomizationExpanded(customization)" (onBeforeToggle)="toggleCustomizationExpand(customization)" styleClass="mb-3">
                  <ng-template pTemplate="header">
                    <div class="row-start-center gap-3 w-full">
                      <span class="font-semibold">{{ customization.fabricCode }}</span>
                      <span class="text-gray-500">|</span>
                      <span>{{ getColorName(customization.colorReference ?? '') }}</span>
                      <span class="text-gray-500">|</span>
                      <span>{{ customization.productionCardNumber }}</span>
                      <p-tag [value]="getCustomizationRollCount(customization) + ' rolls'" severity="secondary" class="ml-2" />
                      <span class="ml-2 text-sm text-gray-500">
                        {{ getCustomizationTotalLbs(customization) | number: '1.2-2' }} lbs / {{ getCustomizationTotalYds(customization) | number: '1.2-2' }} yds
                      </span>
                      <span class="ml-auto mr-2">
                        <span class="text-sm mr-1">{{ 'Dispatch_Alias_Reference' | translateWF: 'Alias' }}:</span>
                        <input
                          type="text"
                          pInputText
                          [(ngModel)]="customization.aliasReference"
                          placeholder="{{ 'Dispatch_Default_Alias' | translateWF: 'Default' }}"
                          style="width: 140px"
                          (click)="$event.stopPropagation()"
                        />
                      </span>
                    </div>
                  </ng-template>
                  <p-card data-variant="compact">
                    <div class="grid-1">
                      <!-- Available rolls for selection -->
                      @if (getAvailableRollsForCustomization(customization).length > 0) {
                        <div>
                          <h4 class="text-sm font-semibold mb-2">{{ 'Dispatch_Available_Rolls' | translateWF: 'Available Rolls' }}</h4>
                          <p-table
                            [value]="getAvailableRollsForCustomization(customization)"
                            [scrollable]="true"
                            scrollHeight="250px"
                            selectionMode="multiple"
                            [selection]="getSelectedCustomizationRolls(customization)"
                            (selectionChange)="onCustomizationRollSelect(customization, $event)"
                            dataKey="data.rollNumber"
                          >
                            <ng-template pTemplate="header">
                              <tr>
                                <th style="width: 3rem">
                                  <p-tableHeaderCheckbox />
                                </th>
                                <th>{{ 'Dispatch_Roll_Number' | translateWF: 'Roll' }}</th>
                                <th class="text-right">{{ 'Dispatch_Weight_Lbs' | translateWF: 'LBS' }}</th>
                                <th class="text-right">{{ 'Dispatch_Weight_Yds' | translateWF: 'YDS' }}</th>
                              </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-rollNode>
                              <tr>
                                <td>
                                  <p-tableCheckbox [value]="rollNode" />
                                </td>
                                <td>{{ rollNode.data.rollNumber }}</td>
                                <td class="text-right">{{ rollNode.data.weightInLbs | number: '1.2-2' }}</td>
                                <td class="text-right">{{ rollNode.data.yards | number: '1.2-2' }}</td>
                              </tr>
                            </ng-template>
                          </p-table>

                          <!-- Selection summary and add split -->
                          <div class="row-start-center gap-3 mt-2 p-2 bg-gray-50 rounded">
                            <p-tag
                              [value]="
                                getSelectedCustomizationRollsCount(customization) + ' rolls | ' + (getSelectedCustomizationRollsWeight(customization) | number: '1.2-2') + ' lbs'
                              "
                              severity="info"
                            />
                            <span class="text-sm">{{ 'Dispatch_Unit_Price' | translateWF: 'Unit Price' }}:</span>
                            <p-inputNumber
                              [ngModel]="getSplitUnitPrice(customization)"
                              (ngModelChange)="setSplitUnitPrice(customization, $event)"
                              mode="decimal"
                              [minFractionDigits]="2"
                              [maxFractionDigits]="4"
                              [placeholder]="'Dispatch_Price' | translateWF: 'Price'"
                              [inputStyle]="{ width: '120px' }"
                            />
                            <p-button
                              [label]="'Dispatch_Add_Split' | translateWF: 'Add Split'"
                              icon="pi pi-plus"
                              size="small"
                              [disabled]="getSelectedCustomizationRollsCount(customization) === 0"
                              (onClick)="addSplitFromSelection(customization)"
                            />
                          </div>
                        </div>
                      } @else {
                        <p class="text-sm text-gray-500 italic">
                          {{ 'Dispatch_All_Rolls_Assigned' | translateWF: 'All rolls are assigned to splits.' }}
                        </p>
                      }

                      <!-- Created splits -->
                      @if (customization.splits.length > 0) {
                        <div>
                          <h4 class="text-sm font-semibold mb-2">{{ 'Dispatch_Splits' | translateWF: 'Splits' }}</h4>
                          @for (split of customization.splits; track $index) {
                            <div class="row-start-center gap-3 p-2 mb-1 bg-blue-50 rounded border border-blue-200">
                              <span class="font-semibold text-sm">{{ 'Dispatch_Split' | translateWF: 'Split' }} {{ $index + 1 }}:</span>
                              <span class="text-sm">
                                {{ getSplitRollsInfo(customization, split).count }} {{ 'Dispatch_Rolls_Lowercase' | translateWF: 'rolls' }} |
                                {{ getSplitRollsInfo(customization, split).lbs | number: '1.2-2' }} lbs | {{ getSplitRollsInfo(customization, split).yds | number: '1.2-2' }} yds
                              </span>
                              <span class="text-sm">&#64;</span>
                              <p-inputNumber
                                [(ngModel)]="split.unitPrice"
                                mode="decimal"
                                [minFractionDigits]="2"
                                [maxFractionDigits]="4"
                                [placeholder]="'Dispatch_Price' | translateWF: 'Price'"
                                [inputStyle]="{ width: '100px' }"
                              />
                              <span class="text-sm font-semibold ml-auto"> ${{ getSplitTotal(customization, split) | number: '1.2-2' }} </span>
                              <p-button
                                icon="pi pi-trash"
                                [text]="true"
                                [rounded]="true"
                                size="small"
                                severity="danger"
                                (onClick)="removeSplit(customization, $index)"
                                [pTooltip]="'Dispatch_Remove_Split' | translateWF: 'Remove split'"
                              />
                            </div>
                            <div class="pl-6 pb-2 text-xs text-gray-500">
                              {{ split.rollNumbers.join(', ') }}
                            </div>
                          }
                        </div>
                      }
                    </div>
                  </p-card>
                </p-panel>
              }
            } @else {
              <p-message severity="info" [text]="'Dispatch_No_Fabrics' | translateWF: 'No fabrics found in this dispatch instruction'" />
            }

            <ng-template pTemplate="footer">
              <div class="row-between-stretch w-full">
                <p-button [label]="'Common_Cancel' | translateWF: 'Cancel'" icon="pi pi-times" variant="outlined" (onClick)="onCloseFabricCustomizationsDialog()" />
                <p-button [label]="'Common_Save' | translateWF: 'Save'" icon="pi pi-check" (onClick)="onSaveFabricCustomizations()" [loading]="isLoading() > 0" />
              </div>
            </ng-template>
          </p-dialog>

          <!-- Priority Order Dialog -->
          <p-dialog
            [header]="
              'Dispatch_Priority_Order' | translateWF: 'Priority Order for ' + (dispatchInstruction()?.dateAndPriorityKey?.date || (priorityDate.value | date: 'yyyy-MM-dd'))
            "
            [(visible)]="showPriorityDialog"
            [modal]="true"
            [style]="{ width: '650px' }"
            [closable]="true"
            (onHide)="closePriorityDialog()"
          >
            <loading-overlay [isLoading]="isPriorityLoading()" />

            @if (priorityListForDate().length > 0) {
              <p class="mb-4 text-gray-600">
                {{
                  'Dispatch_Priority_Instructions'
                    | translateWF
                      : 'Use the arrows to change the priority of the current
            dispatch instruction.'
                }}
              </p>

              <p-table [value]="priorityListForDate()" [scrollable]="true" scrollHeight="400px">
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width: 80px">{{ 'Dispatch_Priority' | translateWF: 'Priority' }}</th>
                    <th>{{ 'Dispatch_Number' | translateWF: 'Number' }}</th>
                    <th>{{ 'Dispatch_Client_Reference' | translateWF: 'Client' }}</th>
                    <th style="width: 120px" class="text-center">{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-instruction let-i="rowIndex">
                  <tr [class.surface-primary]="isCurrentInstruction(instruction)" [class.font-bold]="isCurrentInstruction(instruction)">
                    <td>
                      <span class="text-lg font-semibold">#{{ i + 1 }}</span>
                    </td>
                    <td>
                      <div class="row-sm-start-center">
                        {{ instruction.number }}
                        @if (isCurrentInstruction(instruction)) {
                          <p-tag severity="info" [value]="'Dispatch_Current' | translateWF: 'Current'" />
                        }
                      </div>
                    </td>
                    <td>
                      <small class="text-gray-500">{{ instruction.clientName || instruction.clientReference }}</small>
                    </td>
                    <td class="text-center">
                      @if (isCurrentInstruction(instruction)) {
                        <p-button icon="pi pi-arrow-up" variant="text" [disabled]="i === 0" (onClick)="onMoveUp(i)" [pTooltip]="'Common_Move_Up' | translateWF: 'Move Up'" />
                        <p-button
                          icon="pi pi-arrow-down"
                          variant="text"
                          [disabled]="i === priorityListForDate().length - 1"
                          (onClick)="onMoveDown(i)"
                          [pTooltip]="'Common_Move_Down' | translateWF: 'Move Down'"
                        />
                      }
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            } @else {
              <p-message severity="info" [text]="'Dispatch_No_Instructions_For_Date' | translateWF: 'No dispatch instructions for this date'" />
            }

            <ng-template pTemplate="footer">
              <div class="row-between-center w-full">
                <p-button [label]="'Common_Cancel' | translateWF: 'Cancel'" icon="pi pi-times" variant="outlined" (onClick)="closePriorityDialog()" />
                <div class="row-sm-start-center">
                  @if (hasPriorityChanged()) {
                    <span class="text-orange-500 text-sm"><i class="pi pi-exclamation-circle mr-1"></i>{{ 'Common_Changes_Not_Saved' | translateWF: 'Changes Not Saved' }}</span>
                  }
                  <p-button
                    [label]="'Common_Save' | translateWF: 'Save'"
                    icon="pi pi-check"
                    (onClick)="onSavePriorityOrder()"
                    [loading]="isPriorityLoading()"
                    [disabled]="!hasPriorityChanged()"
                  />
                </div>
              </div>
            </ng-template>
          </p-dialog>

          <!-- Scanning Section (only in Scan Mode) -->
          @if (isScanMode() && canScan() && !isChemicalType()) {
            <div class="p-4 surface-ground border-round">
              <div class="row-between-center mb-3">
                <h3 class="mb-0">
                  <i class="pi pi-barcode mr-2"></i>
                  {{ 'Dispatch_Scan_Roll' | translateWF: 'Scan Roll' }}
                </h3>
                <div class="row-start-center gap-3">
                  <span class="text-sm font-semibold" [class.text-primary]="scanMode() === 'nexus'">NEXUS QR</span>
                  <p-toggleSwitch [ngModel]="scanMode() === 'edr'" (ngModelChange)="scanMode.set($event ? 'edr' : 'nexus')" />
                  <span class="text-sm font-semibold" [class.text-primary]="scanMode() === 'edr'">EDR Barcode</span>
                </div>
              </div>
              @if (scanMode() === 'edr') {
                <p-message
                  severity="info"
                  class="mb-3"
                  [text]="'Dispatch_EDR_Mode_Info' | translateWF: 'EDR mode: Scanning by EDR Number. Success messages will show longer with roll number.'"
                />
              }
              <div class="row-start-center">
                <input
                  type="text"
                  pInputText
                  class="flex-grow-1"
                  [placeholder]="
                    scanMode() === 'edr'
                      ? ('Dispatch_Scan_EDR_Placeholder' | translateWF: 'Enter or scan EDR barcode')
                      : ('Dispatch_Scan_Placeholder' | translateWF: 'Enter or scan roll number')
                  "
                  [ngModel]="scanInput()"
                  (ngModelChange)="scanInput.set($event)"
                  (keyup.enter)="onManualScan()"
                />
                <p-button [label]="'Dispatch_Scan_Button' | translateWF: 'Scan'" icon="pi pi-check" (onClick)="onManualScan()" [disabled]="!scanInput()" />
              </div>
              <div class="mt-4">
                <div class="row-between-stretch mb-2">
                  <span>{{ 'Dispatch_Scan_Progress' | translateWF: 'Scan Progress' }}</span>
                  <span>{{ scannedRollCount() }} / {{ totalRollCount() }}</span>
                </div>
                <p-progressBar [value]="scanProgress()" [showValue]="false" [style]="{ height: '12px' }" />
              </div>
            </div>
          }

          <!-- Entry Management (Add Mode) - Hidden for BGrade and Chemical types -->
          @if (isAddMode() && canEdit() && !isBGradeType() && !isChemicalType()) {
            <p-panel [header]="'Dispatch_Entry_Management' | translateWF: 'Entry Management'" [toggleable]="true">
              <p-card data-variant="flat">
                @if (isEditingEntry()) {
                  <p-message
                    severity="info"
                    class="w-full"
                    [text]="
                      'Dispatch_Edit_Entry_Info'
                        | translateWF: 'Only the Client Product Order can be changed. If work order or color needs to change, please delete this entry and create a new one.'
                    "
                  />
                }
                <form [formGroup]="entryForm" class="grid-12">
                  <div class="span-4">
                    <div class="form-box">
                      <label>{{ 'Dispatch_Work_Order' | translateWF: 'Work Order' }}</label>
                      <p-autoComplete
                        formControlName="workOrder"
                        #workOrderSearchRef="cslServerSearch"
                        [cslServerSearch]="fetchWorkOrders"
                        [suggestions]="workOrderSearchRef.results()"
                        (onSelect)="onWorkOrderSelect($event.value)"
                        (onClear)="onWorkOrderClear()"
                        field="orderNumber"
                        [dropdown]="true"
                        [forceSelection]="true"
                        [disabled]="isEditingEntry()"
                        placeholder="{{ 'Dispatch_Search_Work_Order' | translateWF: 'Search work order...' }}"
                        fluid
                        class="w-full"
                        [ngClass]="{ 'ng-invalid ng-dirty': entryForm.controls.workOrder.invalid && entryForm.controls.workOrder.touched }"
                      >
                        <ng-template let-workOrder pTemplate="item">
                          <div class="col">
                            <span class="font-semibold">{{ workOrder.orderNumber }}</span>
                            <small class="text-gray-500">{{ workOrder.customer?.name }}</small>
                          </div>
                        </ng-template>
                      </p-autoComplete>
                    </div>
                  </div>

                  <div class="span-4">
                    <div class="form-box">
                      <label>{{ 'Dispatch_Client_PO' | translateWF: 'Client Product Order' }}</label>
                      <p-select
                        formControlName="clientProductOrder"
                        [options]="clientPOOptions()"
                        optionLabel="productOrderNumber"
                        (onChange)="onClientPOSelect($event.value)"
                        [disabled]="!selectedWorkOrder()"
                        placeholder="{{ 'Dispatch_Select_Client_PO' | translateWF: 'Select client PO' }}"
                        class="w-full"
                        [ngClass]="{
                          'ng-invalid ng-dirty': entryForm.controls.clientProductOrder.invalid && entryForm.controls.clientProductOrder.touched,
                        }"
                      >
                        <ng-template let-po pTemplate="item">
                          <div class="col">
                            <span>{{ po.productOrderNumber }}</span>
                            @if (po.clientOrderReference) {
                              <small class="text-gray-500">{{ po.clientOrderReference }}</small>
                            }
                          </div>
                        </ng-template>
                      </p-select>
                    </div>
                  </div>

                  <div class="span-4">
                    <div class="form-box">
                      <label>{{ 'Dispatch_Color_Reference' | translateWF: 'Color Reference' }}</label>
                      <p-select
                        formControlName="colorReference"
                        [options]="colorOptions()"
                        optionValue="colorReference"
                        [disabled]="!selectedClientPO() || isEditingEntry()"
                        placeholder="{{ 'Dispatch_Select_Color' | translateWF: 'Select color' }}"
                        class="w-full"
                        [ngClass]="{
                          'ng-invalid ng-dirty': entryForm.controls.colorReference.invalid && entryForm.controls.colorReference.touched,
                        }"
                      >
                        <ng-template let-color pTemplate="selectedItem">
                          @if (color) {
                            <ng-container *ngTemplateOutlet="colorLabel; context: { $implicit: color.colorReference }" />
                          }
                        </ng-template>
                        <ng-template let-color pTemplate="item">
                          <ng-container *ngTemplateOutlet="colorLabel; context: { $implicit: color.colorReference }" />
                        </ng-template>
                      </p-select>
                    </div>
                  </div>

                  <div class="span-12 row-sm justify-end">
                    <p-button [label]="'Common_Clear' | translateWF: 'Clear'" variant="outlined" (onClick)="onAddEntry()" />
                    <p-button
                      [label]="isEditingEntry() ? ('Common_Update' | translateWF: 'Update') : ('Common_Add' | translateWF: 'Add')"
                      icon="pi pi-plus"
                      (onClick)="onSaveEntry()"
                      [disabled]="entryForm.invalid"
                    />
                  </div>
                </form>
              </p-card>
            </p-panel>
          }

          <p-panel [header]="'Dispatch_Entries' | translateWF: 'Dispatch Entries'">
            <p-card data-variant="compact">
              @if (lines().length > 0) {
                <p-table [value]="lines()" selectionMode="single" [selection]="selectedLine()" (onRowSelect)="onRowSelectForRolls($event.data)">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ 'Dispatch_Work_Order' | translateWF: 'Work Order' }}</th>
                      <th>{{ 'Dispatch_Client_PO' | translateWF: 'Client PO' }}</th>
                      @if (!isBGradeOrGreigeOnly()) {
                        <th>{{ 'Dispatch_Color' | translateWF: 'Color' }}</th>
                      }
                      <th>{{ 'Dispatch_Rolls' | translateWF: 'Rolls' }}</th>
                      @if (canEdit() && !isBGradeType()) {
                        <th class="text-right">{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
                      }
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-line>
                    <tr [pSelectableRow]="line" [class.selected-row]="selectedLine()?.lineId === line.lineId">
                      <td>
                        <span class="font-semibold">{{ line.workOrderNumber }}</span>
                        @if (line.workOrder) {
                          <br />
                          <small class="text-gray-500">{{ line.workOrder.customer?.name }}</small>
                        }
                      </td>
                      <td>{{ line.clientProductOrderNumber }}</td>
                      @if (!isBGradeOrGreigeOnly()) {
                        <td>
                          <ng-container *ngTemplateOutlet="colorLabel; context: { $implicit: line.colorReference }" />
                        </td>
                      }
                      <td>
                        @for (fabric of line.fabricsAndBalances; track fabric.fabricCode) {
                          <p-tag severity="info" class="mr-1"> {{ fabric.fabricCode }}: {{ fabric.scannedRolls.length }} </p-tag>
                        }
                      </td>
                      @if (canEdit() && !isBGradeType()) {
                        <td class="text-right">
                          <p-button icon="pi pi-pencil" variant="text" (onClick)="onEditEntry(line)" [pTooltip]="'Common_Edit' | translateWF: 'Edit'" />
                          <p-button
                            icon="pi pi-trash"
                            variant="text"
                            severity="danger"
                            (onClick)="onRemoveEntry(line.lineId)"
                            [pTooltip]="'Common_Delete' | translateWF: 'Delete'"
                          />
                        </td>
                      }
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="emptymessage">
                    <tr>
                      <td [attr.colspan]="isBGradeOrGreigeOnly() ? 3 : 5" class="text-center py-4">
                        {{ 'Dispatch_No_Entries' | translateWF: 'No entries added yet' }}
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              } @else {
                <p-message severity="info" [text]="'Dispatch_No_Entries' | translateWF: 'No entries added yet'" />
              }
            </p-card>
          </p-panel>
        }

        <!-- Chemical Entry Panel (Add Mode) -->
        @if (isAddMode() && canEdit() && isChemicalType() && dispatchInstruction()?.id) {
          <p-panel [header]="'Dispatch_Chemical_Entry' | translateWF: 'Chemical Entry'" [toggleable]="true">
            <p-card data-variant="compact">
              <form [formGroup]="chemicalForm" class="grid-12">
                <div class="span-12">
                  <div class="form-box">
                    <label>{{ 'Dispatch_Client_PO' | translateWF: 'Client PO' }}</label>
                    <p-autoComplete
                      fluid
                      class="w-full"
                      formControlName="clientPurchaseOrder"
                      #clientPoSearchRef="cslServerSearch"
                      [cslServerSearch]="fetchChemicalClientPOs"
                      [suggestions]="clientPoSearchRef.results()"
                      (onSelect)="onChemicalClientPOSelected($event.value)"
                      (onClear)="onChemicalClientPOSelected(null)"
                      [forceSelection]="true"
                      field="number"
                      [delay]="300"
                      [dropdown]="true"
                      appendTo="body"
                    />
                  </div>
                </div>
              </form>

              <!-- Client PO items: pick one to see its available lots -->
              @if (selectedChemicalClientPO()) {
                <div class="mt-4">
                  <h4 class="mb-2">
                    <i class="pi pi-list mr-2"></i>
                    {{ 'Dispatch_PO_Items' | translateWF: 'PO Items' }}
                  </h4>
                  @if (chemicalItemOptions().length > 0) {
                    <p-table [value]="chemicalItemOptions()" selectionMode="single" (onRowSelect)="onSelectChemicalEntryItem($event.data)" styleClass="p-datatable-sm">
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ 'Dispatch_Item' | translateWF: 'Item' }}</th>
                          <th>{{ 'Dispatch_Recipe' | translateWF: 'Recipe' }}</th>
                          <th class="text-right">{{ 'Dispatch_Quantity' | translateWF: 'Quantity' }}</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-row>
                        <tr [pSelectableRow]="row" [class.selected-row]="chemicalEntryItemCode() === row.itemCode">
                          <td>
                            <span class="font-semibold">({{ row.itemCode }})</span>
                            {{ getChemicalItemName(row.itemCode) }}
                          </td>
                          <td>
                            @if (row.productRecipeNumber) {
                              <span class="font-semibold">({{ row.productRecipeNumber }})</span>
                              {{ getChemicalRecipeName(row.productRecipeNumber) }}
                            } @else {
                              —
                            }
                          </td>
                          <td class="text-right">{{ row.quantity | number: '1.2-2' }} {{ getChemicalItemUom(row.itemCode) }}</td>
                        </tr>
                      </ng-template>
                    </p-table>
                  } @else {
                    <p-message severity="info" [text]="'Dispatch_No_PO_Items' | translateWF: 'This Client PO has no items.'" />
                  }
                </div>
              }

              <!-- Available lots for the selected item code: add directly (creates the line if needed) -->
              @if (chemicalEntryItemCode()) {
                <div class="mt-4">
                  <h4 class="mb-2">
                    <i class="pi pi-box mr-2"></i>
                    {{ 'Dispatch_Available_Lot_Items' | translateWF: 'Available Lot Items' }}
                    <small class="text-gray-500 ml-2">{{ chemicalEntryItemCode() }}</small>
                  </h4>
                  @if (entryAvailableChemicalLotItems().length > 0) {
                    <p-table [value]="entryAvailableChemicalLotItems()" styleClass="p-datatable-sm">
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ 'Dispatch_Lot_Item_Number' | translateWF: 'Lot Item' }}</th>
                          <th>{{ 'Dispatch_Item' | translateWF: 'Item' }}</th>
                          <th>{{ 'Dispatch_Batch' | translateWF: 'Batch' }}</th>
                          <th>{{ 'Dispatch_Mfr_Lot_No' | translateWF: 'Mfr. Lot No.' }}</th>
                          <th>{{ 'Dispatch_Location' | translateWF: 'Location' }}</th>
                          <th class="text-right">{{ 'Dispatch_Net_Quantity' | translateWF: 'Net Quantity' }}</th>
                          <th class="text-right">{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-row>
                        <tr>
                          <td>{{ row.number }}</td>
                          <td>
                            <span class="font-semibold">({{ row.itemCode }})</span>
                            {{ getChemicalItemName(row.itemCode) }}
                          </td>
                          <td>
                            @if (row.batchNumber) {
                              {{ row.batchNumber }}
                            } @else {
                              <span class="text-gray-500">{{ 'Dispatch_No_Batch' | translateWF: 'No Batch' }}</span>
                            }
                          </td>
                          <td>{{ row.providerLotId || '—' }}</td>
                          <td>{{ row.currentLocationCode }}</td>
                          <td class="text-right">{{ row.netQuantity | number: '1.2-2' }} {{ getChemicalItemUom(row.itemCode) }}</td>
                          <td class="text-right">
                            <p-button
                              icon="pi pi-plus"
                              variant="text"
                              [label]="'Common_Add' | translateWF: 'Add'"
                              (onClick)="onAddChemicalLotItemFromEntry(row)"
                              [disabled]="assignedChemicalLotItemNumbers().has(row.number) || chemicalForm.invalid"
                            />
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  } @else {
                    <p-message severity="info" [text]="'Dispatch_No_Available_Lot_Items' | translateWF: 'No available lot items for the selected item code.'" />
                  }
                </div>
              }
            </p-card>
          </p-panel>
        }

        <!-- Chemical Entries Table (all modes when Chemical) -->
        @if (isChemicalType()) {
          <h3 class="mb-3">
            <i class="pi pi-list mr-2"></i>
            {{ 'Dispatch_Chemical_Lines' | translateWF: 'Chemical Lines' }}
          </h3>
          @if (chemicalLineSummaries().length > 0) {
            <p-table [value]="chemicalLineSummaries()" selectionMode="single" [selection]="selectedChemicalLine()" (onRowSelect)="onSelectChemicalLine($event.data.line)">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'Dispatch_Item' | translateWF: 'Item' }}</th>
                  <th>{{ 'Dispatch_Lot_Items' | translateWF: 'Lot Items' }}</th>
                  <th class="text-right">{{ 'Dispatch_Total_Quantity' | translateWF: 'Total' }}</th>
                  @if (canEdit() && isAddMode()) {
                    <th class="text-right">{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr [pSelectableRow]="row" [class.selected-row]="selectedChemicalLineId() === row.line.lineId">
                  <td>
                    <span class="font-semibold">{{ row.line.itemCode }}</span>
                    @if (row.itemName) {
                      <br />
                      <small>{{ row.itemName }}</small>
                    }
                    @if (row.recipeNumber) {
                      <br />
                      <small class="text-gray-500">{{ 'Dispatch_Recipe' | translateWF: 'Recipe' }}: {{ row.recipeNumber }}</small>
                    }
                    @if (row.line.clientPurchaseOrderNumber) {
                      <br />
                      <small class="text-gray-500">{{ 'Dispatch_Client_PO' | translateWF: 'Client PO' }}: {{ row.line.clientPurchaseOrderNumber }}</small>
                    } @else {
                      <br />
                      <small class="text-gray-500">{{ 'Dispatch_No_Client_PO' | translateWF: 'No Client PO' }}</small>
                    }
                  </td>
                  <td>
                    <p-tag severity="info">{{ row.lotItemCount }}</p-tag>
                  </td>
                  <td class="text-right">{{ row.totalQuantity | number: '1.2-2' }} {{ getChemicalItemUom(row.line.itemCode) }}</td>
                  @if (canEdit() && isAddMode()) {
                    <td class="text-right">
                      <p-button
                        icon="pi pi-trash"
                        variant="text"
                        severity="danger"
                        (onClick)="onRemoveChemicalLine(row.line); $event.stopPropagation()"
                        [pTooltip]="'Common_Delete' | translateWF: 'Delete'"
                      />
                    </td>
                  }
                </tr>
              </ng-template>
            </p-table>
          } @else {
            <p-message severity="info" [text]="'Dispatch_No_Chemical_Lines' | translateWF: 'No chemical lot items added yet'" />
          }
        }

        <!-- Lot Items in Dispatch (selected chemical line) -->
        @if (isChemicalType() && selectedChemicalLine()) {
          <h3 class="mb-3">
            <i class="pi pi-box mr-2"></i>
            {{ 'Dispatch_Lot_Items_In_Dispatch' | translateWF: 'Lot Items in Dispatch' }}
            <small class="text-gray-500 ml-2">{{ selectedChemicalLine()?.itemCode }}</small>
          </h3>
          @if (selectedLineLotItemsByBatch().length > 0) {
            @for (group of selectedLineLotItemsByBatch(); track group.batchNumber) {
              <p-panel [toggleable]="true" class="mb-3">
                <ng-template pTemplate="header">
                  <div class="flex align-items-center gap-2">
                    <i class="pi pi-tag text-primary"></i>
                    <span class="font-semibold">
                      {{ group.batchNumber || ('Dispatch_No_Batch' | translateWF: 'No Batch') }}
                    </span>
                    <p-tag [value]="group.items.length + ' ' + ('Dispatch_Lot_Items' | translateWF: 'lot items')" severity="info" class="ml-2" />
                    <span class="text-gray-500 ml-2">{{ group.totalQuantity | number: '1.2-2' }} {{ getChemicalItemUom(selectedChemicalLine()?.itemCode) }}</span>
                  </div>
                </ng-template>
                <p-card data-variant="compact">
                  <p-table [value]="group.items">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ 'Dispatch_Lot_Item_Number' | translateWF: 'Lot Item' }}</th>
                        <th>{{ 'Dispatch_Mfr_Lot_No' | translateWF: 'Mfr. Lot No.' }}</th>
                        <th>{{ 'Dispatch_Location' | translateWF: 'Location' }}</th>
                        <th class="text-right">{{ 'Dispatch_Quantity' | translateWF: 'Quantity' }}</th>
                        @if (canEdit() && isAddMode()) {
                          <th class="text-right">{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
                        }
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-item>
                      <tr>
                        <td>{{ item.lotItemNumber }}</td>
                        <td>{{ item.manufacturerLotNumber || '—' }}</td>
                        <td>{{ item.locationCode || '—' }}</td>
                        <td class="text-right">{{ item.quantity | number: '1.2-2' }} {{ getChemicalItemUom(selectedChemicalLine()?.itemCode) }}</td>
                        @if (canEdit() && isAddMode()) {
                          <td class="text-right">
                            <p-button
                              icon="pi pi-trash"
                              variant="text"
                              severity="danger"
                              (onClick)="onRemoveChemicalLotItem(selectedChemicalLine()!, item.lotItemNumber)"
                              [pTooltip]="'Common_Delete' | translateWF: 'Delete'"
                            />
                          </td>
                        }
                      </tr>
                    </ng-template>
                  </p-table>
                </p-card>
              </p-panel>
            }
          } @else {
            <p-message severity="info" [text]="'Dispatch_No_Lot_Items_In_Line' | translateWF: 'No lot items in this line yet'" />
          }
        }

        <!-- Available Chemical Lot Items (Add Mode, with selected chemical line) -->
        @if (isAddMode() && canEdit() && isChemicalType() && selectedChemicalLine()) {
          <h3 class="mb-3">
            <i class="pi pi-box mr-2"></i>
            {{ 'Dispatch_Available_Lot_Items' | translateWF: 'Available Lot Items' }}
            <small class="text-gray-500 ml-2">{{ selectedChemicalLine()?.itemCode }}</small>
          </h3>
          @if (groupedChemicalLotItems().length > 0) {
            <p-accordion [multiple]="true">
              @for (group of groupedChemicalLotItems(); track group.batchNumber) {
                <p-accordionTab [header]="(group.batchNumber || ('Dispatch_No_Batch' | translateWF: 'No Batch')) + ' (' + group.items.length + ')'">
                  <p-table [value]="group.items">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ 'Dispatch_Lot_Item_Number' | translateWF: 'Lot Item' }}</th>
                        <th>{{ 'Dispatch_Mfr_Lot_No' | translateWF: 'Mfr. Lot No.' }}</th>
                        <th>{{ 'Dispatch_Location' | translateWF: 'Location' }}</th>
                        <th class="text-right">{{ 'Dispatch_Net_Quantity' | translateWF: 'Net Quantity' }}</th>
                        <th class="text-right">{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                      <tr>
                        <td>{{ row.number }}</td>
                        <td>{{ row.providerLotId || '—' }}</td>
                        <td>{{ row.currentLocationCode }}</td>
                        <td class="text-right">{{ row.netQuantity | number: '1.2-2' }} {{ getChemicalItemUom(row.itemCode) }}</td>
                        <td class="text-right">
                          <p-button
                            icon="pi pi-plus"
                            variant="text"
                            [label]="'Common_Add' | translateWF: 'Add'"
                            (onClick)="onAddChemicalLotItem(row)"
                            [disabled]="assignedChemicalLotItemNumbers().has(row.number)"
                          />
                        </td>
                      </tr>
                    </ng-template>
                  </p-table>
                </p-accordionTab>
              }
            </p-accordion>
          } @else {
            <p-message severity="info" [text]="'Dispatch_No_Available_Lot_Items' | translateWF: 'No available lot items for the selected item code.'" />
          }
        }

        <!-- Available Rolls TreeTable (Add Mode with selected entry) -->
        @if (isAddMode() && selectedLine() && !isChemicalType()) {
          @if (hasAvailableRolls()) {
            <div class="row-between-center mb-3">
              <h3>
                <i class="pi pi-box mr-2"></i>
                {{ 'Dispatch_Available_Rolls' | translateWF: 'Available Rolls' }}
                @if (isBGradePaged()) {
                  <span class="text-gray-500 font-normal ml-2">({{ availableRollsTotalRecords() | number }})</span>
                }
              </h3>
              <div class="row-start-center gap-3">
                @if (selectedRollNodes.length > 0) {
                  <div class="row-sm-start-center px-3 py-2 surface-ground border-round">
                    <i class="pi pi-check-square text-primary"></i>
                    <span class="font-semibold">{{ getSelectedRollsCount() }}</span>
                    <span class="text-gray-500">{{ 'Dispatch_Rolls' | translateWF: 'rolls' }}</span>
                    <span class="mx-1">|</span>
                    <span class="font-semibold">{{ getSelectedRollsWeight() | number: '1.2-2' }}</span>
                    <span class="text-gray-500">lbs</span>
                    <span class="mx-1">|</span>
                    <span class="font-semibold">{{ getSelectedRollsYards() | number: '1.2-2' }}</span>
                    <span class="text-gray-500">yds</span>
                  </div>
                }
                <p-button
                  [label]="'Dispatch_Add_Selected' | translateWF: 'Add Selected Rolls'"
                  icon="pi pi-plus"
                  (onClick)="onAddSelectedRolls()"
                  [disabled]="selectedRollNodes.length === 0"
                />
              </div>
            </div>
            <div class="row-between-center gap-3 mb-3">
              <span class="p-input-icon-left w-full md:w-1/3">
                <i class="pi pi-search"></i>
                <input
                  type="text"
                  pInputText
                  class="w-full"
                  [placeholder]="'Dispatch_Search_Rolls' | translateWF: 'Search by roll number or PC...'"
                  [ngModel]="availableRollsSearchTerm()"
                  (ngModelChange)="onAvailableRollsSearchChange($event)"
                />
              </span>
              <p-selectButton
                [options]="qualityFilterOptions"
                [ngModel]="selectedQualityFilter()"
                (ngModelChange)="selectedQualityFilter.set($event)"
                optionLabel="label"
                optionValue="value"
                unselectable="true"
              />
            </div>
            <app-dispatch-rolls-tree-table
              [treeData]="filteredAvailableRollsTree()"
              [config]="availableRollsConfig"
              [(selectedNodes)]="selectedRollNodes"
              [getColorName]="getColorNameBound"
              [lbsToKg]="lbsToKgBound"
              [getFabricByCode]="getFabricByCodeBound"
              [fabricFormValues]="fabricFormValues"
              [lazy]="isBGradePaged()"
              [paginator]="showAvailableRollsPaginator()"
              [rows]="availableRollsPageSize()"
              [first]="availableRollsFirst()"
              [totalRecords]="availableRollsTotalRecords()"
              (lazyLoad)="onAvailableRollsPage($event)"
            />
          } @else {
            <p-message
              severity="info"
              [text]="
                'Dispatch_No_Available_Rolls_For_Entry' | translateWF: 'No available rolls found for this work order and color. Make sure rolls exist in Finished Goods warehouse.'
              "
            />
          }
        }

        <!-- Rolls in Dispatch -->
        @if (selectedLine() && !isChemicalType()) {
          @if (scannedRollsByFabric().length > 0) {
            @for (fabricGroup of scannedRollsByFabric(); track fabricGroup.fabricCode) {
              <p-panel [toggleable]="true" class="mb-3">
                <ng-template pTemplate="header">
                  <div class="flex align-items-center gap-2">
                    <i class="pi pi-tag text-primary"></i>
                    <span class="font-semibold">{{ fabricGroup.fabricCode }}</span>
                    @if (fabricGroup.fabricDescription) {
                      <span class="text-gray-600">- {{ fabricGroup.fabricDescription }}</span>
                    }
                    <p-tag [value]="fabricGroup.rollCount + ' rolls'" severity="info" class="ml-2" />
                    <span class="text-gray-500 ml-2">
                      {{ fabricGroup.totalWeight | number: '1.2-2' }} lbs / {{ lbsToKg(fabricGroup.totalWeight) | number: '1.2-2' }} kg
                      @if (fabricGroup.totalYards > 0) {
                        / {{ fabricGroup.totalYards | number: '1.2-2' }} yds
                      }
                    </span>
                    @if (getSelectedScannedRollsCount(fabricGroup.fabricCode) > 0) {
                      <p-button
                        [label]="'Dispatch_Delete_Selected' | translateWF: 'Delete Selected'"
                        [badge]="getSelectedScannedRollsCount(fabricGroup.fabricCode).toString()"
                        icon="pi pi-trash"
                        severity="danger"
                        size="small"
                        class="ml-auto"
                        (click)="$event.stopPropagation()"
                        (onClick)="onDeleteSelectedScannedRolls(selectedLine()!.lineId, fabricGroup.fabricCode)"
                      />
                    }
                  </div>
                </ng-template>
                <p-card data-variant="compact">
                  <app-dispatch-rolls-tree-table
                    [treeData]="fabricGroup.pcTree"
                    [config]="scannedRollsConfig"
                    [selectedNodes]="getSelectedScannedRollNodes(fabricGroup.fabricCode)"
                    (selectedNodesChange)="onScannedRollSelectionChange(fabricGroup.fabricCode, $event)"
                    [getColorName]="getColorNameBound"
                    [lbsToKg]="lbsToKgBound"
                    (rollDeleted)="onRemoveRoll(selectedLine()!.lineId, $event.rollNumber)"
                  />
                </p-card>
              </p-panel>
            }
          } @else {
            <p-message severity="info" [text]="'Dispatch_No_Rolls' | translateWF: 'No rolls in this entry'" />
          }
        }

        <!-- End of @if (!isNewRecord()) -->
      </div>

      <!-- Right Column: Sticky Fabric Balance Panel (only shown when NOT a new record) -->
      @if (!isNewRecord()) {
        <div class="span-12-4-4 scroller-y sticky">
          <p-panel [header]="'Dispatch_Fabric_Balance' | translateWF: 'Fabric Balance'">
            <p-card data-variant="flat">
              @if (fabricBalances().length > 0) {
                @for (balance of fabricBalances(); track balance.fabricCode + balance.colorReference) {
                  <p-fieldset [legend]="balance.fabricCode" styleClass="cs-fieldset-nested" class="block mb-3">
                    <div class="text-sm text-gray-500 mb-1">{{ getFabricDescription(balance.fabricCode) }}</div>
                    @if (!isBGradeOrGreigeOnly()) {
                      <div class="text-sm text-gray-500 mb-2">{{ getColorName(balance.colorReference) }}</div>
                    }
                    <div class="grid-2-sm">
                      <div class="text-sm">
                        <div class="text-gray-500">{{ 'Dispatch_Rolls' | translateWF: 'Rolls' }}</div>
                        <div class="font-semibold text-lg">{{ balance.rollCount }}</div>
                      </div>
                      <div class="text-sm">
                        <div class="text-gray-500">{{ 'Dispatch_Total_Weight' | translateWF: 'Total' }}</div>
                        <div class="font-semibold text-lg">{{ balance.totalInDispatch | number: '1.2-2' }} lbs</div>
                      </div>
                    </div>
                    <div class="row-between-stretch text-gray-500 text-sm mt-2">
                      <span>{{ lbsToKg(balance.totalInDispatch) | number: '1.2-2' }} kg</span>
                      @if (balance.totalYards > 0) {
                        <span>{{ balance.totalYards | number: '1.2-2' }} yds</span>
                      }
                    </div>
                    @if (getFabricColorCompletion(balance.fabricCode, balance.colorReference); as completion) {
                      <div class="mt-2">
                        <div class="row-between-stretch text-xs text-gray-500 mb-1">
                          <span>{{ 'Dispatch_Completion' | translateWF: 'Completion' }}</span>
                          <span class="font-semibold">{{ completion.completionPercentage | number: '1.2-2' }}%</span>
                        </div>
                        <p-progressBar [value]="Math.min(100, completion.completionPercentage)" [showValue]="false" [style]="{ height: '8px' }" />
                      </div>
                    }
                  </p-fieldset>
                }

                <div class="total-summary mt-4 p-3 surface-primary border-round">
                  <div class="font-semibold mb-2">{{ 'Dispatch_Total_Summary' | translateWF: 'Total Summary' }}</div>
                  <div class="grid-2-sm">
                    <div>
                      <div class="text-sm opacity-80">{{ 'Dispatch_Total_Rolls' | translateWF: 'Total Rolls' }}</div>
                      <div class="font-semibold text-xl">{{ totalRollCount() }}</div>
                    </div>
                    <div>
                      <div class="text-sm opacity-80">{{ 'Dispatch_Total_Weight' | translateWF: 'Total Weight' }}</div>
                      <div class="font-semibold text-xl">{{ getTotalWeight() | number: '1.2-2' }} lbs</div>
                    </div>
                  </div>
                  <div class="row-between-stretch opacity-80 text-sm mt-1">
                    <span>{{ lbsToKg(getTotalWeight()) | number: '1.2-2' }} kg</span>
                    @if (getTotalYards() > 0) {
                      <span>{{ getTotalYards() | number: '1.2-2' }} yds</span>
                    }
                  </div>
                </div>
              } @else {
                <p-message severity="secondary" [text]="'Dispatch_No_Fabric_Data' | translateWF: 'No fabric data available'" />
              }

              <!-- Scan Progress (shown in scan mode) -->
              @if (isScanMode()) {
                <div class="scan-status mt-4 p-3 surface-ground border-round">
                  <div class="font-semibold mb-2">{{ 'Dispatch_Scan_Status' | translateWF: 'Scan Status' }}</div>
                  <div class="row-between-center mb-2">
                    <span>{{ 'Dispatch_Scanned' | translateWF: 'Scanned' }}</span>
                    <span class="font-semibold">{{ scannedRollCount() }} / {{ totalRollCount() }}</span>
                  </div>
                  <p-progressBar [value]="scanProgress()" [showValue]="true" [style]="{ height: '24px' }" />
                  @if (scanProgress() === 100) {
                    <p-message severity="success" [text]="'Dispatch_All_Scanned' | translateWF: 'All rolls scanned!'" styleClass="mt-3" />
                  }
                </div>
              }
            </p-card>
          </p-panel>

          <!-- !Work Order Progress section -->
          @if (selectedLine() && selectLineWorkOrder() > 0) {
            <div class="work-order-progress mt-4 p-3 rounded-lg shadow-2xl bg-white">
              <h4 class="font-semibold mb-3">
                <i class="pi pi-chart-line mr-2"></i>
                {{ 'Dispatch_Work_Order_Progress' | translateWF: 'Work Order Progress' }}
              </h4>

              <div class="mb-3 text-sm">
                <div class="text-gray-600">{{ 'Dispatch_Work_Order' | translateWF: 'Work Order' }}:</div>
                <div class="font-semibold">{{ selectedLine()?.workOrderNumber }}</div>

                <div class="text-gray-600 mt-1">{{ 'Dispatch_Color' | translateWF: 'Color' }}:</div>
                <div class="font-semibold">{{ getColorName(selectedLine()?.colorReference ?? '') }}</div>

                <div class="text-gray-600 mt-1">{{ 'Dispatch_Client_PO' | translateWF: 'Client PO' }}:</div>
                <div class="font-semibold">{{ selectedLine()?.clientProductOrderNumber }}</div>
              </div>

              <div class="divider my-3"></div>

              <div class="mb-3 p-2 surface-card border-round">
                <div class="text-sm text-gray-600">{{ 'Dispatch_WO_Required' | translateWF: 'Work Order Requires' }}</div>
                <div class="text-xl font-bold text-primary">{{ selectLineWorkOrder() | number: '1.2-2' }} {{ workOrderUomLabel() }}</div>
                <div class="text-xs text-gray-500" [class.hidden]="isWorkOrderInYards()">{{ lbsToKg(selectLineWorkOrder()) | number: '1.2-2' }} kg</div>
              </div>

              @if (dispatchesCompleted().length > 0 || dispatchesPending().length > 0) {
                <div class="mb-3">
                  <div class="text-sm font-semibold mb-2">
                    {{ 'Dispatch_Other_Instructions' | translateWF: 'Other Dispatch Instructions' }}
                  </div>

                  @for (di of dispatchesCompleted(); track di.dispatchInstructionReference) {
                    <div class="row-between-center p-2 mb-1 surface-card border-round text-sm">
                      <div class="row-sm-start-center">
                        <i class="pi pi-check-circle text-green-500"></i>
                        <span class="font-mono">{{ di.dispatchInstructionNumber }}</span>
                        <p-tag severity="success" [value]="'Dispatch_Completed' | translateWF: 'Completed'" />
                      </div>
                      <div class="font-semibold">{{ dispatchQuantityInUom(di) | number: '1.2-2' }} {{ workOrderUomLabel() }}</div>
                    </div>
                  }

                  @for (di of dispatchesPending(); track di.dispatchInstructionReference) {
                    <div class="row-between-center p-2 mb-1 surface-card border-round text-sm">
                      <div class="row-sm-start-center">
                        <i class="pi pi-clock text-orange-500"></i>
                        <span class="font-mono">{{ di.dispatchInstructionNumber }}</span>
                        <p-tag severity="warn" [value]="'Dispatch_Pending' | translateWF: 'Pending'" />
                      </div>
                      <div class="font-semibold">{{ dispatchQuantityInUom(di) | number: '1.2-2' }} {{ workOrderUomLabel() }}</div>
                    </div>
                  }
                </div>
              }

              @if (currentLineWeight() > 0) {
                <div class="mb-3 p-2 border-2 border-primary border-round">
                  <div class="row-between-center text-sm">
                    <div class="row-sm-start-center">
                      <i class="pi pi-arrow-right text-primary"></i>
                      <span class="font-semibold">{{ 'Dispatch_Current_Selection' | translateWF: 'Current Selection' }}</span>
                    </div>
                    <div class="font-bold text-primary">{{ currentLineWeight() | number: '1.2-2' }} {{ workOrderUomLabel() }}</div>
                  </div>
                </div>
              }

              <div class="divider my-3"></div>

              <div class="my-3">
                <div class="text-sm font-semibold mb-2 row-between-stretch">
                  <span>{{ 'Dispatch_Progress' | translateWF: 'Progress' }}</span>
                  <span [class]="isOverDispatched() ? 'text-red-500' : 'text-primary'">{{ workOrderProgressPercentage() | number: '1.0-0' }}%</span>
                </div>

                <div class="relative w-full h-6 bg-gray-200 border-round overflow-hidden">
                  @if (totalWeightCompleted() > 0) {
                    <div
                      class="absolute h-full bg-green-500 row-center-center text-white text-xs font-semibold transition-all"
                      [style.width.%]="Math.max(2, progressBarPercentage(totalWeightCompleted()))"
                      [style.left.%]="0"
                      [title]="'Completed: ' + (totalWeightCompleted() | number: '1.2-2') + ' ' + workOrderUomLabel()"
                    >
                      @if (progressBarPercentage(totalWeightCompleted()) > 5) {
                        {{ totalWeightCompleted() | number: '1.0-0' }}
                      }
                    </div>
                  }

                  @if (totalWeightPending() > 0) {
                    <div
                      class="absolute h-full bg-orange-500 row-center-center text-white text-xs font-semibold transition-all"
                      [style.width.%]="Math.max(2, progressBarPercentage(totalWeightPending()))"
                      [style.left.%]="progressBarPercentage(totalWeightCompleted())"
                      [title]="'Pending: ' + (totalWeightPending() | number: '1.2-2') + ' ' + workOrderUomLabel()"
                    >
                      @if (progressBarPercentage(totalWeightPending()) > 5) {
                        {{ totalWeightPending() | number: '1.0-0' }}
                      }
                    </div>
                  }

                  @if (currentLineWeight() > 0) {
                    <div
                      class="absolute h-full bg-blue-500 row-center-center text-white text-xs font-semibold transition-all"
                      [style.width.%]="Math.max(2, progressBarPercentage(currentLineWeight()))"
                      [style.left.%]="progressBarPercentage(totalWeightCompleted() + totalWeightPending())"
                      [title]="'Selected: ' + (currentLineWeight() | number: '1.2-2') + ' ' + workOrderUomLabel()"
                    >
                      @if (progressBarPercentage(currentLineWeight()) > 5) {
                        {{ currentLineWeight() | number: '1.0-0' }}
                      }
                    </div>
                  }

                  @if (isOverDispatched()) {
                    <div
                      class="absolute h-full bg-red-500 opacity-40 transition-all"
                      [style.left.%]="requiredMarkerPercentage()"
                      [style.width.%]="100 - requiredMarkerPercentage()"
                      [title]="('Dispatch_Excess' | translateWF: 'Excess') + ': ' + (excessWeight() | number: '1.2-2') + ' ' + workOrderUomLabel()"
                    ></div>
                    <div class="absolute h-full bg-red-700" [style.left.%]="requiredMarkerPercentage()" [style.width.px]="2"></div>
                  }
                </div>

                <div class="row-between-stretch mt-2 text-xs text-gray-600">
                  <span class="font-semibold" [class]="isOverDispatched() ? 'text-red-500' : 'text-primary'"
                    >{{ totalWeightDispatched() | number: '1.2-2' }} {{ workOrderUomLabel() }}</span
                  >
                  <span>{{ selectLineWorkOrder() | number: '1.0-0' }} {{ workOrderUomLabel() }}</span>
                </div>
              </div>

              <div class="row mt-2 text-xs">
                <div class="row-start-center gap-1">
                  <div class="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>{{ 'Dispatch_Sent' | translateWF: 'Sent' }}</span>
                </div>
                <div class="row-start-center gap-1">
                  <div class="w-3 h-3 bg-orange-500 rounded-full"></div>
                  <span>{{ 'Dispatch_Pending' | translateWF: 'Pending' }}</span>
                </div>
                <div class="row-start-center gap-1">
                  <div class="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span>{{ 'Dispatch_Selected' | translateWF: 'Selected' }}</span>
                </div>
              </div>

              <div class="grid-2-sm text-sm">
                <div class="p-2 surface-card border-round">
                  <div class="text-gray-600 text-xs">{{ 'Dispatch_Total_Dispatched' | translateWF: 'Total' }}</div>
                  <div class="font-bold">{{ totalWeightDispatched() | number: '1.2-2' }} {{ workOrderUomLabel() }}</div>
                  <div class="text-xs" [class]="isOverDispatched() ? 'text-red-500' : 'text-primary'">{{ workOrderProgressPercentage() | number: '1.0-0' }}%</div>
                  @if (isYardsEstimateUnavailable()) {
                    <div
                      class="text-xs text-orange-500 row-sm-start-center"
                      [pTooltip]="'Dispatch_Yards_Estimate_Unavailable_Tooltip' | translateWF: 'The work order has no pounds to convert weight to yards, so rolls without a measured length count as 0'"
                    >
                      <i class="pi pi-exclamation-triangle"></i>
                      <span>{{ 'Dispatch_Yards_Estimate_Unavailable' | translateWF: 'Yards estimate unavailable' }}</span>
                    </div>
                  } @else if (hasEstimatedYards()) {
                    <div
                      class="text-xs text-gray-500 row-sm-start-center"
                      [pTooltip]="'Dispatch_Estimated_Yards_Tooltip' | translateWF: 'Rolls without a measured length are converted from weight using the work order ratio'"
                    >
                      <i class="pi pi-info-circle"></i>
                      <span>{{ 'Dispatch_Includes_Estimated_Yards' | translateWF: 'Includes estimated yards' }}</span>
                    </div>
                  }
                </div>

                @if (isOverDispatched()) {
                  <div class="p-2 surface-card border-round border-1 border-red-500">
                    <div class="text-red-500 text-xs row-sm-start-center">
                      <i class="pi pi-exclamation-triangle"></i>
                      <span>{{ 'Dispatch_Excess' | translateWF: 'Excess' }}</span>
                    </div>
                    <div class="font-bold text-red-500">{{ excessWeight() | number: '1.2-2' }} {{ workOrderUomLabel() }}</div>
                    <div class="text-xs text-gray-500" [class.hidden]="isWorkOrderInYards()">{{ lbsToKg(excessWeight()) | number: '1.2-2' }} kg</div>
                  </div>
                } @else {
                  <div class="p-2 surface-card border-round">
                    <div class="text-gray-600 text-xs">{{ 'Dispatch_Remaining' | translateWF: 'Remaining' }}</div>
                    <div class="font-bold">{{ remainingWeight() | number: '1.2-2' }} {{ workOrderUomLabel() }}</div>
                    <div class="text-xs text-gray-500" [class.hidden]="isWorkOrderInYards()">{{ lbsToKg(remainingWeight()) | number: '1.2-2' }} kg</div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
      <!-- End of @if (!isNewRecord()) for sidebar -->
    </div>
  </div>
</main>

<!-- Color name with its approved recipe (lab dip) number: color dropdown and Entries table -->
<ng-template #colorLabel let-colorId>
  <span class="inline-flex items-center gap-3">
    <span class="inline-flex items-center gap-1">
      <i class="pi pi-palette text-gray-500" [pTooltip]="'Dispatch_Color' | translateWF: 'Color'"></i>
      {{ getColorName(colorId) }}
    </span>
    @if (getColorApprovedRecipeNumber(colorId); as recipeNumber) {
      <span class="inline-flex items-center gap-1">
        <i class="pi pi-file-check text-gray-500" [pTooltip]="'Dispatch_Approved_Recipe_Lab_Dip' | translateWF: 'Approved Recipe / Lab Dip'"></i>
        {{ recipeNumber }}
      </span>
    } @else {
      <span class="text-gray-500">{{ 'Dispatch_No_Approved_Recipe' | translateWF: 'No Approved Recipe' }}</span>
    }
  </span>
</ng-template>
